export function isUpperCase(ch: string) {
  return ch.toUpperCase() == ch;
}

export function clamp(min: number, max: number, val: number) {
  return Math.max(min, Math.min(max, val));
}

export function toggle<T>(array: T[], value: T): T[] {
  const existsAtIndex = array.indexOf(value);
  let mutated = [...array];
  if (existsAtIndex !== -1) {
    mutated.splice(existsAtIndex, 1);
    return mutated;
  }
  return [...mutated, value];
}

export function replace<T>(
  array: T[],
  replace: T,
  fallback: 'push' | 'pushStart' | 'none' = 'none',
  comparator?: ((a: { id?: any }, replace: T) => boolean) | keyof T
): T[] {
  let mutated = (!array || !array.length) ? [] : [...array]

  let comparedProperty = 'id';

  if (typeof comparator == 'string') {
    comparedProperty = comparator;
  }

  const i = array.findIndex(e => (
    typeof comparator === "function" ? comparator(e, replace) : e[comparedProperty] === replace[comparedProperty])
  );

  if (i !== -1) {
    mutated[i] = replace;
  } else if (fallback === "push") {
    mutated.push(replace);
  } else if (fallback === "pushStart") {
    mutated = [replace, ...mutated];
  }

  return mutated;
}

/**
 * Checks equality by comparing the stringified value.
 * @returns Whether two stringified representations are equal.
 */
export function eq(one: any, theOther: any) {
  return JSON.stringify(one) == JSON.stringify(theOther);
}

/**
 * Uses `eq` to compare values present in `partial`.
 */
export function partialEq<T>(partial: Partial<T>, target: T) {
  for (let key in partial) {
    if (!eq(partial[key], target[key])) {
      return false;
    }
  }

  return true;
}

/**
 * Checks equality by comparing specific properties of two objects.
 */
export function propsEq<T>(one: T, theOther: T, properties: (keyof T)[]) {
  for (let prop of properties) {
    if (one[prop] !== theOther[prop]) {
      return false;
    }
  }

  return true;
}

export function map<T>(length: number, cb: (index: number) => T): T[] {
  let result: T[] = [];

  for (let i = 0; i < length; i++) {
    result.push(cb(i));
  }

  return result;
}

/**
 * @returns Whether any shallow-level property evaluates to true.
 */
export function hasTruthyProperty(obj: any) {
  if (!obj || typeof obj != 'object') {
    return false;
  }

  for (let key in obj) {
    if (obj[key]) {
      return true;
    }
  }

  return false;
}

/**
 * Extracts coordinates from either a touch or mouse event.
 */
export function getClientXY(ev: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>) {
  let clientX = 0, clientY = 0

  if (ev) {
    if ("clientX" in ev) {
      clientX = ev.clientX;
      clientY = ev.clientY;
    } else {
      let touch = ev.touches.item(0)
      if (touch) {
        clientX = touch.clientX
        clientY = touch.clientY
      }
    }
  }

  return {
    clientX,
    clientY
  }
}

/** Event for both mouse & touch. */
export type DivPressEvent = React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>

export function mergeFormData(...data: FormData[]): FormData {
  if (!data || !data.length) {
    return null;
  }

  if (data.length == 1) {
    return data[0]
  }

  let fd = new FormData()

  for (let d of data) {
    let entries = d.entries();
    let entry: IteratorResult<[string, FormDataEntryValue]>;
    while (true) {
      entry = entries.next();

      if (entry == null || entry.done) {
        break;
      }

      fd.set(entry.value[0], entry.value[1])
    }
  }

  return fd
}

/**
 * @returns Whether a point intersects a rectangle
 */
export function intersects(point: { x: number, y: number }, dst: { left: number, top: number, right: number, bottom: number }) {
  return point.x > dst.left && point.x < dst.right && point.y > dst.top && point.y < dst.bottom;
}

/**
 * @returns Whether to dates overlap.
 */
export function overlaps(xStart: Date, xEnd: Date, yStart: Date, yEnd: Date) {
  if (!xStart || !xEnd || !yStart || !yEnd) {
    return false;
  }

  if (xStart.getTime() == yStart.getTime() && xEnd.getTime() == yEnd.getTime()) {
    return true;
  }

  if (xStart.getTime() < yStart.getTime()) {
    return xEnd.getTime() > yStart.getTime();
  }

  if (xStart.getTime() > yStart.getTime()) {
    return xStart.getTime() < yEnd.getTime();
  }

  return false;
}

export function isEnterKey(ev: React.KeyboardEvent) {
  return [13].indexOf(ev.keyCode) != -1;
}

export function isEscapeKey(ev: React.KeyboardEvent) {
  return [27].indexOf(ev.keyCode) != -1;
}


export function isTabKey(ev: React.KeyboardEvent) {
  return [9].indexOf(ev.keyCode) != -1;
}

/**
 * Pads a string, shorthand for padStart.
 */
export function pad(str: string | number, length: number = 2, fill: string = "0") {
  return str.toString().padStart(length, fill);
}

/**
 * Pre-fetches an image.
 * @see https://stackoverflow.com/questions/3646036/javascript-preloading-images
 */
export function preFetchImage(src: string) {
  var img = new Image();
  img.src = src;
}

export function isDateValid(d: Date) {
  return d instanceof Date && !isNaN(d as any)
}

export function nullIfNaN<T extends string | number>(val: T): T {
  return isNaN(val as any) ? null : val;
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function choose<T>(a: T[]) {
  return a[Math.floor(Math.random() * a.length)]
}

export type FormatCurrencyOpts = {
  trimFractionDigits?: boolean;
  includeCurrency?: boolean;
}

/**
 * Formats to a local currency.
 */
export function formatCurrency(amount: number, locale = 'sv', opts: FormatCurrencyOpts = {}) {
  let currency = { "sv": "kr" }[locale]
  if (opts.includeCurrency === undefined) {
    opts.includeCurrency = true;
  }

  let formatted = `${(amount || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${opts.includeCurrency ? currency : ''}`;

  if (opts.trimFractionDigits) {
    formatted = formatted.replace(/([,.])([1-9+])?(0+)/, '$1$2');
  }

  //  Remove trailing delimiter
  formatted = formatted.replace(/[,.]\s/, ' ');

  return formatted;
}

/**
 * @returns An element-unique ID
 */
export function getUniqueId(prefix?: string) {
  if (prefix) {
    return `${prefix}-${getUniqueId(null)}`;
  }

  return (Date.now() + Math.floor(100000 * Math.random())).toString();
}

export function unique<T>(array: T[]): T[] {
  //  @ts-ignore
  return [...new Set(array)];
}

export function generateAgreementKey() {
  const key = (Date.now() + Math.random()).toString(24).replace(/[.-]/g, '');
  return key;
}